import { cityMapping } from "city-timezones";

export type Place = {
  city: string;
  country: string;
  province: string;
  iana: string;
  lat: number;
  lng: number;
  pop: number;
  aliases: string[];
};

export const COLORS = [
  "#ccff00",
  "#38bdf8",
  "#f472b6",
  "#a78bfa",
  "#fb923c",
  "#2dd4bf",
];

const EXTRA_ALIASES: { city: string; iana?: string; aliases: string[] }[] = [
  { city: "Las Vegas", iana: "America/Los_Angeles", aliases: ["vegas"] },
  { city: "New York", aliases: ["nyc", "ny", "est", "edt", "et", "eastern"] },
  { city: "Toronto", aliases: ["est", "edt", "et", "eastern", "toronto time"] },
  { city: "Boston", aliases: ["est", "edt", "et"] },
  { city: "Miami", aliases: ["est", "edt", "et"] },
  { city: "Chicago", aliases: ["cst", "cdt", "ct", "central"] },
  { city: "Mexico City", aliases: ["cst", "cdt", "ct"] },
  { city: "Denver", aliases: ["mst", "mdt", "mt", "mountain"] },
  { city: "Phoenix", aliases: ["mst", "arizona"] },
  { city: "Los Angeles", aliases: ["pst", "pdt", "pt", "pacific", "la"] },
  { city: "San Francisco", aliases: ["pst", "pdt", "pt", "sf"] },
  { city: "Seattle", aliases: ["pst", "pdt", "pt"] },
  { city: "Vancouver", aliases: ["pst", "pdt", "pt"] },
  { city: "Honolulu", aliases: ["hst", "hawaii"] },
  { city: "London", aliases: ["gmt", "bst", "wet", "uk"] },
  { city: "Berlin", aliases: ["cet", "cest", "central european"] },
  { city: "Paris", aliases: ["cet", "cest"] },
  { city: "Amsterdam", aliases: ["cet", "cest"] },
  { city: "Madrid", aliases: ["cet", "cest"] },
  { city: "Rome", aliases: ["cet", "cest"] },
  { city: "Zurich", aliases: ["cet", "cest"] },
  { city: "Stockholm", aliases: ["cet", "cest"] },
  { city: "Warsaw", aliases: ["cet", "cest"] },
  { city: "Tokyo", aliases: ["jst", "japan"] },
  { city: "Mumbai", aliases: ["ist", "india"] },
  { city: "Delhi", aliases: ["ist", "india", "new delhi"] },
  { city: "Bengaluru", aliases: ["ist", "bangalore"] },
  { city: "Sydney", aliases: ["aest", "aedt", "aet"] },
  { city: "Melbourne", aliases: ["aest", "aedt"] },
  { city: "Auckland", aliases: ["nzst", "nzdt"] },
  { city: "Dubai", aliases: ["gst"] },
  { city: "Singapore", aliases: ["sgt"] },
];

function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/['’.]/g, "");
}

function prettyCountry(country: string): string {
  if (country === "United States of America") return "United States";
  return country;
}

function aliasesFor(city: string, iana: string): string[] {
  const folded = fold(city);
  const found = EXTRA_ALIASES.filter(
    (row) => fold(row.city) === folded && (!row.iana || row.iana === iana),
  );
  return found.flatMap((row) => row.aliases);
}

export const PLACES: Place[] = cityMapping
  .filter((row) => Boolean(row.timezone))
  .map((row) => ({
    city: row.city,
    country: prettyCountry(row.country),
    province: row.province ?? "",
    iana: row.timezone,
    lat: row.lat,
    lng: row.lng,
    pop: row.pop ?? 0,
    aliases: aliasesFor(row.city, row.timezone),
  }));

export function placeId(place: Pick<Place, "iana" | "city" | "province">): string {
  return `${place.iana}|${place.city}|${place.province}`;
}

export function searchPlaces(query: string, limit = 10): Place[] {
  const q = fold(query.trim());
  if (!q) return [];
  const scored: { place: Place; score: number }[] = [];
  for (const place of PLACES) {
    const city = fold(place.city);
    const tokens = city.split(/[\s/-]+/).filter(Boolean);
    const province = fold(place.province);
    const country = fold(place.country);
    const iana = fold(place.iana).replace(/_/g, " ");
    let score = 0;
    if (city === q) score = 100;
    else if (tokens.includes(q)) score = 94;
    else if (place.aliases.some((a) => fold(a) === q)) score = 90;
    else if (city.startsWith(q) || tokens.some((t) => t.startsWith(q))) score = 86;
    else if (place.aliases.some((a) => fold(a).startsWith(q))) score = 78;
    else if (city.includes(q) || tokens.some((t) => t.includes(q))) score = 70;
    else if (place.aliases.some((a) => fold(a).includes(q))) score = 60;
    else if (province.startsWith(q) || province.includes(q)) score = 42;
    else if (country.startsWith(q)) score = 28;
    else if (iana.includes(q.replace(/\s+/g, " "))) score = 18;
    if (score > 0) scored.push({ place, score });
  }
  scored.sort(
    (a, b) => b.score - a.score || b.place.pop - a.place.pop || a.place.city.localeCompare(b.place.city),
  );
  return scored.slice(0, limit).map((row) => row.place);
}

export function findPlace(iana: string, city: string, province?: string): Place | undefined {
  if (province) {
    const exact = PLACES.find(
      (p) => p.iana === iana && p.city === city && p.province === province,
    );
    if (exact) return exact;
  }
  return PLACES.find((p) => p.iana === iana && p.city === city);
}

export function placeFromIana(iana: string): Place {
  const matches = PLACES.filter((p) => p.iana === iana).sort((a, b) => b.pop - a.pop);
  if (matches[0]) return matches[0];
  const city = (iana.split("/").pop() ?? iana).replace(/_/g, " ");
  return {
    city,
    country: "",
    province: "",
    iana,
    lat: 0,
    lng: 0,
    pop: 0,
    aliases: [],
  };
}

export function detectLocalPlace(): Place {
  const iana = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return placeFromIana(iana);
}
